import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Timer, Cursor, ArrowBendUpLeft, TrendUp, MapPin,
  Megaphone, CheckCircle,
} from "@phosphor-icons/react";

/**
 * v3.2.47 · Per-campaign drilldown.
 *
 * Route: /admin/visitors/campaign/:name
 * Shows total visits, uniques, sign-up conversions, bounce rate, avg
 * dwell/clicks, source split, landing-path breakdown, and the last
 * 30 visits attributed to this UTM campaign.
 */
export default function AdminCampaignDetailPage() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [window, setWindow] = useState(30);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(
        `/admin/visitors/campaign/${encodeURIComponent(name)}`,
        { params: { days: window } },
      );
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name, window]);

  const fmt = (n) => (n ?? 0).toLocaleString();
  const fmtDwell = (ms) => {
    const s = Math.round((ms || 0) / 1000);
    if (s <= 0) return "0s";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return rs ? `${m}m ${rs}s` : `${m}m`;
  };
  const fmtTime = (iso) => {
    try {
      const dt = new Date(iso);
      const diff = (Date.now() - dt.getTime()) / 1000;
      if (diff < 60)         return `${Math.floor(diff)}s ago`;
      if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400)      return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
      return dt.toISOString().slice(0, 10);
    } catch { return iso; }
  };

  const empty = data && data.total_visits === 0;

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-visitors" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link to="/admin/visitors"
              className="inline-flex items-center gap-2 text-xs font-mono text-gray-400 hover:text-[#00E5FF] uppercase tracking-wider"
              data-testid="campaign-back-link">
          <ArrowLeft size={14} weight="bold" /> back to visitors
        </Link>

        <div className="mt-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-tag inline-flex items-center gap-1">
              <Megaphone size={12} weight="bold" /> // campaign
            </div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter break-all"
                data-testid="campaign-heading">
              #{name}
            </h1>
          </div>
          <select
            value={window}
            onChange={(e) => setWindow(Number(e.target.value))}
            className="bg-black/40 border border-white/10 text-white text-xs font-mono px-3 py-2 focus:border-[#00E5FF] outline-none"
            data-testid="campaign-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {busy && !data ? (
          <div className="mt-10 text-xs font-mono text-gray-500">Loading…</div>
        ) : empty ? (
          <div className="mt-10 border border-white/10 bg-black/40 p-10 text-center"
               data-testid="campaign-empty">
            <Megaphone size={40} weight="duotone" className="mx-auto text-gray-600" />
            <div className="mt-3 text-sm text-white">No traffic for this campaign yet</div>
            <div className="mt-1 text-xs text-gray-500 font-mono">
              Add <span className="text-white">?utm_campaign={name}</span> to
              your outbound links and traffic will attribute here.
            </div>
          </div>
        ) : data ? (
          <>
            {/* Headline tiles */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3"
                 data-testid="campaign-tiles">
              <Tile label="visits"      value={fmt(data.total_visits)} icon={TrendUp} />
              <Tile label="uniques"     value={fmt(data.uniques)}      icon={Users} />
              <Tile label="signups"     value={fmt(data.signups)}      icon={CheckCircle}
                     sub={data.uniques > 0 ? `${(data.conversion_rate * 100).toFixed(1)}% conversion` : ""} />
              <Tile label="avg dwell"   value={fmtDwell(data.avg_dwell_ms)} icon={Timer} />
              <Tile label="bounces"     value={fmt(data.bounces)}      icon={ArrowBendUpLeft}
                     sub={`${(data.bounce_rate * 100).toFixed(0)}% bounce rate`} />
            </div>

            <div className="mt-2 text-xs font-mono text-gray-500">
              avg <span className="text-white">{(data.avg_clicks || 0).toFixed(1)}</span> clicks per engaged pageview
            </div>

            <div className="mt-8 grid md:grid-cols-5 gap-6">
              {/* Left column: landing paths + sources */}
              <div className="md:col-span-2 space-y-4">
                <div className="border border-white/10 bg-black/40 p-5"
                     data-testid="campaign-paths">
                  <div className="label-tag">// landing paths</div>
                  {data.landing_paths.length === 0 ? (
                    <div className="mt-4 text-xs text-gray-500 font-mono">No paths yet.</div>
                  ) : (
                    <ul className="mt-4 divide-y divide-white/5">
                      {data.landing_paths.map((p) => (
                        <li key={p.path}
                            className="py-2 flex items-center justify-between text-sm gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-white font-mono truncate">{p.path}</div>
                            <div className="mt-0.5 text-[10px] font-mono text-gray-500">
                              avg dwell {fmtDwell(p.avg_dwell)}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs font-mono text-gray-400 shrink-0">
                            <span title="unique visitors">
                              <Users size={11} className="inline mr-1 -mt-0.5" />
                              {fmt(p.uniques)}
                            </span>
                            <span className="text-white font-bold w-14 text-right">{fmt(p.visits)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border border-white/10 bg-black/40 p-5"
                     data-testid="campaign-sources">
                  <div className="label-tag">// sources split</div>
                  {data.sources.length === 0 ? (
                    <div className="mt-4 text-xs text-gray-500 font-mono">No sources.</div>
                  ) : (
                    <ul className="mt-4 divide-y divide-white/5">
                      {data.sources.map((s) => (
                        <li key={s.source}
                            className="py-2 flex items-center justify-between text-sm">
                          <span className="font-mono uppercase tracking-wider text-[10px] text-white">
                            {s.source}
                          </span>
                          <span className="text-white font-bold text-xs">{fmt(s.visits)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Right column: recent visits */}
              <div className="md:col-span-3 border border-white/10 bg-black/40 p-5"
                   data-testid="campaign-recent">
                <div className="label-tag">// recent visits</div>
                {data.recent.length === 0 ? (
                  <div className="mt-4 text-xs text-gray-500 font-mono">No visits yet.</div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-gray-500 border-b border-white/10">
                        <tr>
                          <th className="pb-2 pr-3">When</th>
                          <th className="pb-2 pr-3">Path</th>
                          <th className="pb-2 pr-3">Engagement</th>
                          <th className="pb-2 pr-3">Location</th>
                          <th className="pb-2">Auth</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {data.recent.map((r, i) => (
                          <tr key={i} className="align-top">
                            <td className="py-2 pr-3 font-mono text-gray-400 whitespace-nowrap">{fmtTime(r.ts)}</td>
                            <td className="py-2 pr-3 font-mono text-white truncate max-w-[180px]">{r.path}</td>
                            <td className="py-2 pr-3 font-mono text-gray-400 whitespace-nowrap">
                              {r.dwell_ms > 0 ? (
                                <>
                                  <span className="text-gray-200">{fmtDwell(r.dwell_ms)}</span>
                                  <span className="text-gray-600"> · </span>
                                  <span className="text-[#00E5FF]">{r.click_count}c</span>
                                </>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                            <td className="py-2 pr-3 text-gray-300">
                              <span className="inline-flex items-center gap-1">
                                <MapPin size={10} className="text-gray-500" />
                                {r.city ? `${r.city}, ` : ""}{r.country}
                              </span>
                            </td>
                            <td className="py-2 text-[10px] font-mono uppercase">
                              {r.auth ? (
                                <span className="text-emerald-400">signed in</span>
                              ) : (
                                <span className="text-gray-600">guest</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, sub }) {
  return (
    <div className="border border-white/10 bg-black/40 p-4">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
        <Icon size={16} weight="duotone" className="text-[#00E5FF]" />
      </div>
      <div className="mt-3 font-display text-3xl font-black text-white">{value}</div>
      {sub && (
        <div className="mt-1 font-mono text-[10px] text-gray-600 leading-tight">{sub}</div>
      )}
    </div>
  );
}
